import {QueryOptions} from '@apollo/client';
import Backbone from 'backbone';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as navigation from 'Navigation';
import StorableRelevantItemsTabState from 'relevant-items/storables/StorableRelevantItemsTabState';
import {LocationViewQuery, LocationViewQueryVariables} from 'samples/locations/__generated/LocationViewQuery';
import LocationTabState from 'samples/locations/LocationTabState';
import {LOCATION_VIEW_QUERY} from 'samples/locations/LocationViewConstants';

interface LocationFileStateAttributes {
  name?: string;
  archiveRecord?: ArchiveRecord | null;
  editUrl?: string;
}

export default class LocationFileState extends ApolloFileState<
  LocationViewQuery,
  LocationViewQueryVariables,
  LocationFileStateAttributes
> {
  static fileType = ModelTypeConstants.LOCATION;

  static override openFile(fileId: string): JQueryPromise<LocationFileState> {
    return super.openFile(fileId) as JQueryPromise<LocationFileState>;
  }

  static override getQueryOptions(fileId: string): QueryOptions<LocationViewQueryVariables> {
    return {
      query: LOCATION_VIEW_QUERY,
      variables: {
        id: fileId,
      },
    };
  }

  static override dataToAttributes(data: LocationViewQuery): LocationFileStateAttributes {
    const {location} = data;

    if (isInaccessible(location)) {
      return {};
    } else {
      return {
        name: location.name,
        archiveRecord: location.archiveRecord,
        editUrl: `/samples/locations/${location.id}/edit`,
      };
    }
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.SingleBufferStatusBarState(),
    };
  }

  override initialize(...args: Array<unknown>): void {
    super.initialize(...args);
    const buffers = new navigation.BufferStates([
      new navigation.BufferState(),
      new navigation.BufferState({active: false}),
    ]);
    this.set({buffers});
    const buffer = buffers.at(0);
    buffer.tabs().add(new LocationTabState({id: this.id}));
    buffer.tabs().at(0).set({active: true});

    buffer.tabs().add(new StorableRelevantItemsTabState({storableId: this.id}));
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name', () => {
      this.syncTitleAndURL();
    });
  }

  override editURL(): string | undefined {
    return this.get('editUrl');
  }

  override name(): string {
    return this.get('name') || 'Loading...';
  }

  override archiveRecord(): ArchiveRecord | null {
    return this.get('archiveRecord') ?? null;
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    this.set({archiveRecord});
  }
}
